import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
export { default as ActivityTypeBadge } from './ActivityTypeBadge.vue';
export { default as Badge } from './Badge.vue';
export { default as ComboBadge } from './ComboBadge.vue';
export { default as DirectionBadge } from './DirectionBadge.vue';
export { default as DraftBadge } from './DraftBadge.vue';
export { default as FxBadge } from './FxBadge.vue';

const iconFixClasses = `
  *:[[class*="fa-"]]:flex
  *:[[class*="fa-"]]:w-auto
  *:[[class*="fa-"]]:items-center
  *:[[class*="fa-"]]:justify-center
  *:[[class*="fa-"]]:shrink
  [&>[class*="fa-"]:not(.fa-spinner-third)]:translate-x-[0.5px]
`;

const baseBadgeClasses = `
  inline-flex
  items-center
  text-nowrap
  h-fit
  border
  border-transparent
  gap-x-2
  rounded-xs
  font-semibold
  tracking-[0.12em]
  focus:outline-hidden
  focus:ring-2
  focus:ring-ring
  focus:ring-offset-2
  transition-colors
  ${iconFixClasses}`;

const BADGE_BORDERS = {
  accent: 'border-warning-hover',
  agent: 'border-agent-muted',
  amber: 'border-amber-200',
  // TODO DELETE
  blue: 'border-primary-muted-hover',
  danger: 'border-destructive-muted',
  default: 'border-default',
  emerald: 'border-emerald-200',
  destructive: 'border-destructive-muted',
  error: 'border-destructive-muted',
  open: 'border-primary-muted-hover',
  pink: 'border-pink-200',
  primary: 'border-primary-muted-hover',
  secondary: 'border-secondary',
  success: 'border-success-muted',
  warning: 'border-warning-hover',
};

export const badgeVariants = cva(baseBadgeClasses, {
  compoundVariants: [
    ...Object.keys(BADGE_BORDERS).map((variant) => ({
      class: BADGE_BORDERS[variant],
      outline: true as boolean,
      rounded: false as boolean,
      variant: variant as keyof typeof BADGE_BORDERS,
    })),
  ],
  defaultVariants: {
    outline: false,
    rounded: false,
    size: 'sm',
    variant: 'default',
  },
  variants: {
    dashed: {
      true: 'border-dashed',
    },
    outline: {
      true: 'border',
    },
    rounded: {
      true: 'rounded-full!',
    },
    size: {
      '2xs': 'h-4 px-1 leading-tight text-2xs',
      default: 'px-2 h-5 text-2xs',
      lg: 'h-8 px-4 text-xs',
      md: 'h-6 px-2 text-xs',
      sm: 'h-5 px-2 text-2xs',
      xl: 'h-10 px-4 text-xs',
      xs: 'h-5 px-1.5 leading-tight text-3xs',
    },
    variant: {
      accent: `bg-accent text-accent-foreground`,
      agent: 'bg-agent-muted text-agent',
      amber: 'bg-amber-50 text-amber-700',
      blue: 'bg-primary-muted text-primary',
      danger: 'bg-destructive-muted text-destructive',
      default: 'bg-primary-muted text-primary-foreground',
      destructive: 'bg-destructive-muted text-destructive',
      emerald: 'bg-emerald-50 text-emerald-700',
      error: 'bg-destructive text-white',
      open: 'bg-primary-muted text-primary',
      outline: 'border border-border dark:bg-input/30',
      pink: 'bg-pink-100 text-pink-400',
      primary: 'bg-primary-muted text-primary',
      'priority-high': 'bg-destructive text-destructive-foreground',
      'priority-urgent': 'bg-red-700 text-white uppercase!',
      secondary: 'bg-secondary text-secondary-foreground',
      success: 'bg-success-muted text-success',
      warning: `bg-warning-muted text-warning`,
    },
  },
});

export type BadgeVariants = VariantProps<typeof badgeVariants>;
